import React, { PureComponent } from 'react'
import { string, func, shape } from 'prop-types'
import { withI18n } from '@active/react-utils/i18n'
import Input from '@active/react-ui/Input'
import FloatAlert from '@active/react-ui/FloatAlert'
import IntlPhoneInput from '@active/react-ui/IntlPhoneInput'
import Form from '@active/react-ui/Form'
import Button from '@active/react-ui/Button'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import FormStaticField from '@active/react-ui/FormStaticField'
import Skeleton from '@active/react-ui/Skeleton'
import { AddressEditor } from '@active/react-address-editor'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import { generateServices } from '../../shared/service'
import { commonServiceConfig } from '../../widgets/shared/config'
import './UserProfile.scss'
import DobSelectorWithValueParse from '../common/DobSelectorWithValueParse'
import I18nMessage from '../common/I18nMessage'
import { nameValidatorBuilder } from '../../shared/validator'

function tansferISOToDate(aDatetime) { // 2010-01-01T00:00:00.000Z  10/01/1985
  const date = (aDatetime ?? '').split('T')[0]
  const dateArray = date.split('-')
  const aDate = `${dateArray[1]}/${dateArray[2]}/${dateArray[0]}`
  return aDate
}

function tansferDateToISO(date) {
  try {
    const dateArray = date.split('/')
    const aDatetime = `${dateArray[2]}-${dateArray[0]}-${dateArray[1]}T00:00:00.000Z`
    return aDatetime
  } catch (error) {
    return date
  }
}

export const addressFieldsConfig = {
  country: { autoComplete: 'country' },
  city: { autoComplete: 'city' },
  line1: { autoComplete: 'address-line1' },
  line2: { autoComplete: 'address-line2' },
  stateProvince: { autoComplete: 'province' },
  postalCode: { autoComplete: 'postal-code' },
}
export class UserProfile extends PureComponent {
  static propTypes = {
    services: shape({
      getProfile: func.isRequired,
      editProfile: func.isRequired,
    }).isRequired,
    i18n: func.isRequired,
    realm: string,
    login: func.isRequired,
  }

  static defaultProps = {
    realm: 'active',
  }

  constructor(props) {
    super(props)

    this.state = {}

    const { realm, login } = props
    const commonServices = generateServices(commonServiceConfig, {
      ...this.props,
      realm,
    })
    commonServices.getUserInfoBySSOToken({
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      resolve: ({ active_username: activeUsername }) => {
        if (activeUsername) {
          this.getInitUserData(activeUsername)
        } else {
          login()
        }
      },
      reject: () => {
        login()
      },
    })
  }

  getInitUserData = username => {
    const { services } = this.props
    services.getProfile({
      resolve: ({
        firstname, lastname, dob,
        phone, phone_ext: phoneExt,
        country, city, street, address, state, zip,
      }) => {
        this.setState({
          username,
          initialValues: omitBy({
            username,
            firstname,
            lastname,
            phoneNumber: isNil(phone) ? undefined : `${phone}${isNil(phoneExt) ? '' : phoneExt}`,
            dob: tansferDateToISO(dob),
            address: {
              country,
              city,
              line1: street,
              line2: address,
              stateProvince: state,
              postalCode: zip,
            },
          }, isNil),
          disableSave: false,
        })
      },
      reject: message => {
        this.setState({
          errorMessage: message,
          showUpdateError: true,
        }, () => {
          setTimeout(() => {
            this.setState({ showUpdateError: false })
          }, 3000)
        })
      },
    })
  }

  handleSubmit = values => {
    const { services } = this.props
    const { username } = this.state
    this.setState({ disableSave: true })
    const params = {
      username,
      firstname: values.firstname,
      lastname: values.lastname,
      dob: tansferISOToDate(values.dob),
      phone: values.phoneNumber,
      phone_ext: '',
      country: values.address.country,
      city: values.address.city,
      street: values.address.line1,
      address: values.address.line2,
      state: values.address.stateProvince,
      zip: values.address.postalCode,
    }
    if (!params.state) delete params.state
    services.editProfile({
      params,
      resolve: ({ status }) => {
        if (status === 'SUCCESS') {
          this.setState({ showUpdateSuccess: true }, () => {
            setTimeout(() => {
              this.setState({ showUpdateSuccess: false })
            }, 3000)
          })
        }
        this.setState({ disableSave: false })
      },
      reject: message => {
        this.setState({
          errorMessage: message,
          showUpdateError: true,
          disableSave: false,
        }, () => {
          setTimeout(() => {
            this.setState({ showUpdateError: false })
          }, 3000)
        })
      },
    })
  }

  handleChange = formData => {
    const { initialValues } = this.state
    const { address } = formData.values
    this.setState({
      initialValues: {
        ...initialValues,
        address,
      },
    })
  }

  render() {
    const { i18n } = this.props
    const {
      initialValues, showUpdateSuccess, showUpdateError, disableSave, errorMessage,
    } = this.state

    if (!initialValues) {
      return <Skeleton className="passport-skeleton" />
    }

    return (
      <div className="user-profile" id="user-profile">
        {showUpdateSuccess && (
          <FloatAlert theme="inline" type="success">
            <I18nMessage id="passport.user.userProfile.updateSuccess" />
          </FloatAlert>
        )}
        {showUpdateError && (
          <FloatAlert theme="inline" type="error">
            {errorMessage || <I18nMessage id="passport.error.server.default" />}
          </FloatAlert>
        )}
        <div className="user-profile-title">
          <h1 className="h-style-clean">
            <I18nMessage id="passport.userProfile.title" />
          </h1>
        </div>
        <Form
          className="user-profile-form"
          horizontal
          trimInput
          onSubmit={this.handleSubmit}
          onChange={this.handleChange}
          initialValues={initialValues}
        >
          <div className="required-tip">
            {i18n('passport.common.asterisk.significance')}
            <span className="an-abbr-required-mark an-require-label__abbr" title="Required">*</span>
          </div>
          <div className="username">
            <FormGroup name="username">
              <FormLabel verticalStart><I18nMessage id="passport.user.userProfile.userName" /></FormLabel>
              <FormStaticField />
            </FormGroup>
          </div>
          <div className="form-row">
            <FormGroup name="firstname" required>
              <FormLabel><I18nMessage id="passport.lib.components.common.contact.field.firstName" /></FormLabel>
              <FormField validate={nameValidatorBuilder(i18n, 'passport.error.validation.firstNamePrompt')} as={Input} autoComplete="given-name" type="text" />
            </FormGroup>
            <FormGroup className="form-group-second" name="lastname" required>
              <FormLabel><I18nMessage id="passport.lib.components.common.contact.field.lastName" /></FormLabel>
              <FormField validate={nameValidatorBuilder(i18n, 'passport.error.validation.lastNamePrompt')} as={Input} autoComplete="family-name" type="text" />
            </FormGroup>
          </div>
          <FormGroup name="dob" required>
            <FormLabel><I18nMessage id="passport.user.userProfile.dateOfBirth" /></FormLabel>
            <FormField as={DobSelectorWithValueParse} />
          </FormGroup>
          <FormGroup className="phoneNumber" name="phoneNumber">
            <FormLabel><I18nMessage id="passport.user.userProfile.phoneNumber" /></FormLabel>
            <FormField as={IntlPhoneInput} />
          </FormGroup>
          <AddressEditor trimInput required name="address" fieldsConfig={addressFieldsConfig} />
          <div className="save-changes">
            <Button className="save-button" disabled={disableSave} type="primary" submit>
              {i18n('passport.user.userProfile.saveChanges')}
            </Button>
          </div>
        </Form>
      </div>
    )
  }
}

export default withI18n(UserProfile)
